import { useEffect, useState } from "react";

export { exportarExcel } from "./exportarExcel";

// Total de un monto al que se le suma el IVA. `iva` es la tasa del organismo
// en porcentaje (22 = 22%), que las pantallas obtienen con useIva().
export function conIva(monto, iva) {
  return monto * (1 + iva / 100);
}
export { columnasExportablesDesde, textoDesdeNodo } from "./columnasExportables";

// Hook: true si el viewport está en la resolución más chica (mobile).
// Usa 768px para alinearse con los @media (max-width: 768px) del resto del código.
export function useIsMobile(maxWidth = 768) {
  const query = `(max-width: ${maxWidth}px)`;
  const get = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false;
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return isMobile;
}

// Store data in LS
export function storeInLS(key, data) {
  localStorage.setItem(key, data);
}

// Read from LS
export function readFromLS(key) {
  return localStorage.getItem(key);
}

// Delete from LS
export function deleteFromLS(key) {
  return localStorage.removeItem(key);
}

// Portal público del organismo, donde se inicia sesión. Lo fija Estetica al
// cargar /branding: el dominio depende del organismo por el que se entró, así
// que no puede venir de una variable de entorno del build.
let urlPublicaOrganismo = null;

export function setUrlPublicaOrganismo(url) {
  urlPublicaOrganismo = url || null;
}

// A dónde se manda al usuario para que inicie sesión: el portal público del
// organismo, o REACT_APP_LOGIN_URL en las apps que no cargan /branding
// (superadmin) o si /branding falló.
export function urlLogin() {
  return urlPublicaOrganismo || process.env.REACT_APP_LOGIN_URL || "/login";
}

// Operations for logging out the user
export function logout() {
  localStorage.removeItem(process.env.REACT_APP_LS_LOGIN_TOKEN);
  window.location.href = urlLogin();
}

// Formatea una fecha YYYY-MM-DD
/**
 * Formatea un monto numérico con punto como separador de miles
 * y coma para decimales. Solo muestra decimales si existen.
 * Ej: 8000 → "8.000", 9760.50 → "9.760,50"
 */
export function formatMonto(valor) {
  const num = Number(valor);
  if (isNaN(num)) return "-";
  const tieneDecimales = num % 1 !== 0;
  return num.toLocaleString("es-UY", {
    minimumFractionDigits: tieneDecimales ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export function formatFecha(fecha) {
  if (fecha) {
    let fechaFormat = new Date(fecha);
    const fechaProcesada = new Date(
      fechaFormat.getTime() + fechaFormat.getTimezoneOffset() * 60 * 1000,
    );
    return `${fechaProcesada.getFullYear()}-${(
      "0" +
      (fechaProcesada.getMonth() + 1)
    ).slice(-2)}-${("0" + fechaProcesada.getDate()).slice(-2)}`;
  }
}

// Fecha y hora en la zona del navegador, para timestamps reales (a diferencia
// de formatFecha, que trata el valor como fecha sin hora).
export function formatFechaHora(fecha) {
  if (fecha) {
    const f = new Date(fecha);
    const dos = (n) => ("0" + n).slice(-2);
    return `${f.getFullYear()}-${dos(f.getMonth() + 1)}-${dos(
      f.getDate(),
    )} ${dos(f.getHours())}:${dos(f.getMinutes())}`;
  }
}

// URL base de la API.
//
// En multitenant el organismo se resuelve del hostname que recibe la API, así
// que la URL NO puede estar fija: tiene que salir del dominio por el que se
// entró. Así, cambiar el dominio en el navegador cambia de organismo, tanto en
// producción como en desarrollo.
//
// Orden de resolución:
//   1. REACT_APP_API_URL — override explícito. Sirve para apuntar a otra API
//      (por ejemplo staging desde local). Si está seteada, gana.
//   2. REACT_APP_API_PORT — desarrollo: la API corre en otro puerto del MISMO
//      hostname. https://prueba.localhost:3000 pega a prueba.localhost:3001.
//   3. Producción: el frontend y la API comparten dominio y la API cuelga de
//      /api, así que alcanza con el origen actual.
export function urlBaseAPI() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, "");
  }

  if (process.env.REACT_APP_API_PORT) {
    // http y no el protocolo de la página: en desarrollo la API corre sin TLS.
    return `http://${window.location.hostname}:${process.env.REACT_APP_API_PORT}`;
  }

  return `${window.location.origin}/api`;
}

// Access API
// With timeout specified in .env
export function accessAPI(
  verb,
  endpoint,
  data,
  callbackSuccess,
  callbackFail,
  file,
  blobResponse,
  opciones,
) {
  const url = urlBaseAPI() + "/" + endpoint;
  const accessToken = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);

  let fetchConfig = {
    method: verb,
    headers: {
      Authorization: "Bearer " + accessToken,
    },
  };

  let dataParaEnviar;

  // Si hay un archivo para enviar, genera el form data
  if (file) {
    let formData = new FormData();
    if (Array.isArray(file)) {
      // Múltiples archivos en un solo request — todos bajo el mismo field name
      // "archivo" (express-busboy los recibe como array en req.files.archivo).
      file.forEach((f) => formData.append("archivo", f));
    } else {
      formData.append("archivo", file);
    }
    if (data) {
      // Se agregan al form data el resto de los campos del payload
      let datosArray = Object.entries(data);
      for (const [key, value] of datosArray) {
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(key, item));
        } else {
          formData.append(key, value instanceof Date ? value.toISOString() : value);
        }
      }
    }
    dataParaEnviar = formData;
  } else if (data) {
    // Si no hay archivos y es sólo data, se debe agregar el content-type adecuado en el header
    fetchConfig.headers["Content-Type"] = "application/json";
    fetchConfig.headers["accept-encoding"] = "gzip, deflate";
    dataParaEnviar = JSON.stringify(data);
  }
  fetchConfig.body = dataParaEnviar;

  Promise.race([
    // Generate two promises, one with the fetch and the other with the timeout
    // the one that finishes first resolves
    fetch(url, fetchConfig),
    new Promise(function (resolve, reject) {
      setTimeout(
        () => reject(new Error("request timeout")),
        // El default NO es cosmético: sin él, una app a la que le falte la
        // variable recibe undefined, setTimeout lo toma como 0 y el race pierde
        // contra su propio timeout antes de que la API conteste. Se ve como un
        // error de credenciales con un 200 en la pestaña de red.
        // opciones.timeout permite estirar el límite para endpoints que tardan
        // más que un request normal (por ejemplo el asistente de IA).
        opciones?.timeout || process.env.REACT_APP_API_TIMEOUT || 30000,
      );
    }),
  ])
    .then((response) => {
      // When race resolves, it verifies the status of the API response
      // If it's 200 or 201, it was successful, then the success callback is run
      if (response.status === 204) {
        callbackSuccess();
      } else if (response.status >= 200 && response.status < 300) {
        // Si el caller pidió blob, devuelve blob sin importar el Content-Type
        if (blobResponse) {
          response.blob().then((data) => {
            callbackSuccess(data);
          });
        } else if (response.headers.get("Content-Type")?.search("json") !== -1) {
          // Verifica que el body sea un JSON
          // Hace un JSON parse para ver si el formato es JSON, si no es lo agarra el catch
          response.json().then((data) => {
            callbackSuccess(data);
          });
        } else if (
          response.headers.get("Content-Type")?.search("octet-stream") !== -1 ||
          response.headers.get("Content-Type")?.search("image") !== -1
        ) {
          response.blob().then((data) => {
            callbackSuccess(data);
          });
        } else {
          response.text().then((data) => {
            callbackSuccess(data);
          });
        }
      } else {
        if (
          response.headers.get("Content-Type") &&
          response.headers.get("Content-Type").search("json") !== -1
        ) {
          response.json().then((data) => {
            data.status = response.status;
            callbackFail(data);
          });
        } else {
          callbackFail([{ msg: "Error de API" }]);
        }
      }
    })
    .catch((e) => {
      console.log(e);
      var response = {
        status: 500,
        msg: "Ocurrió un error, intente nuevamente más tarde",
      };
      callbackFail(response);
    });
}

// Subida de archivos PESADOS a S3 vía presigned URL (subida directa a S3).
//
// Primitiva de bajo nivel. En la mayoría de los casos NO se llama directo: se usa
// el componente <SubidorArchivos> de shared/components, que la envuelve con UI
// (file picker, barra de progreso, toasts). Llamala directo solo si necesitás un
// control distinto.
//
// A diferencia de accessAPI (que manda el archivo al backend y este lo reenvía
// a S3), acá el archivo va DIRECTO a S3, evitando el timeout del frontend y los
// límites de body del backend/proxy. Flujo:
//   1) pide al backend las URLs prefirmadas (presignEndpoint)
//   2) sube cada archivo con un PUT directo a S3 (sin header Authorization)
//   3) confirma al backend para que cree los registros (confirmarEndpoint)
//
// Recibe un objeto de opciones:
//   presignEndpoint   ej "admin/archivo/presign" (genérico por rol)
//   confirmarEndpoint ej "admin/proceso/123/archivo/confirmar" (propio de la sección)
//   archivos          File | File[]
//   datosPresign      objeto extra a mezclar en el body de presign (ej { ubicacion })
//   datosConfirmar    objeto extra a mezclar en el body de confirmar
//   onProgress        opcional (porcentaje, { numero, total, nombre }) del total
//                     subido y del archivo que se está subiendo ahora
// Devuelve una Promise que resuelve con la respuesta de confirmar, o rechaza con
// un error con shape [{ msg }] (similar a accessAPI).
export function subirArchivosPesadosS3({
  presignEndpoint,
  confirmarEndpoint,
  archivos,
  datosPresign,
  datosConfirmar,
  onProgress,
}) {
  const apiUrl = urlBaseAPI() + "/";
  const accessToken = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);
  const lista = Array.isArray(archivos) ? archivos : [archivos];

  // Llama a un endpoint JSON del backend (presign/confirmar). No usa el timeout
  // de accessAPI porque son requests livianos; el archivo pesado va aparte a S3.
  const llamarBackend = (endpoint, body) =>
    fetch(apiUrl + endpoint, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }).then(async (response) => {
      if (response.status >= 200 && response.status < 300) {
        return response.json();
      }
      let errorData = [{ msg: "Error de API" }];
      try {
        errorData = await response.json();
      } catch (e) {}
      throw errorData;
    });

  // Sube un archivo con PUT directo a S3 usando XHR para poder reportar progreso.
  // IMPORTANTE: no se manda header Authorization, rompería la firma del presigned.
  const subirAS3 = (url, file, onFileProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      if (file.type) xhr.setRequestHeader("Content-Type", file.type);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onFileProgress) {
          onFileProgress(e.loaded, e.total);
        }
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject([{ msg: "Error al subir el archivo a S3" }]);
      xhr.onerror = () => reject([{ msg: "Error de red al subir a S3" }]);
      xhr.send(file);
    });

  return (async () => {
    // 1) Pedir las URLs prefirmadas
    const presignados = await llamarBackend(presignEndpoint, {
      ...(datosPresign || {}),
      archivos: lista.map((f) => ({ filename: f.name })),
    });

    // 2) Subir cada archivo a S3, agregando progreso total
    const tamanioTotal = lista.reduce((acc, f) => acc + f.size, 0) || 1;
    const subidoPorArchivo = new Array(lista.length).fill(0);

    for (let i = 0; i < presignados.length; i++) {
      await subirAS3(presignados[i].url, lista[i], (loaded) => {
        subidoPorArchivo[i] = loaded;
        if (onProgress) {
          const totalSubido = subidoPorArchivo.reduce((a, b) => a + b, 0);
          // 2º argumento: info del archivo que se está subiendo ahora, para
          // poder mostrar su nombre y un contador (cambia archivo por archivo).
          onProgress(Math.round((totalSubido / tamanioTotal) * 100), {
            numero: i + 1,
            total: lista.length,
            nombre: lista[i].name,
          });
        }
      });
      subidoPorArchivo[i] = lista[i].size;
    }

    // 3) Confirmar para que el backend cree los registros Archivo
    return llamarBackend(confirmarEndpoint, {
      ...(datosConfirmar || {}),
      archivos: presignados.map((p) => ({
        nombresistema: p.nombresistema,
        nombredescarga: p.nombredescarga,
      })),
    });
  })().catch((error) => {
    // Normaliza el error al shape [{ msg }] antes de propagarlo
    throw Array.isArray(error)
      ? error
      : [{ msg: "Error al subir el archivo" }];
  });
}

// Descargar archivo
export function downloadFile(
  endpoint,
  filename,
  callbackSuccess,
  callbackFail,
) {
  const url = urlBaseAPI() + "/" + endpoint;
  const accessToken = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);
  var fetchConfig = {
    method: "GET",
    headers: {
      "accept-encoding": "gzip, deflate",
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
    },
  };
  Promise.race([
    // Genera dos promesas, una con el fetch y otra con el timeout
    // La primera que termine, resuelve
    fetch(url, fetchConfig),
    new Promise(function (resolve, reject) {
      setTimeout(
        () => reject(new Error("request timeout")),
        // El default NO es cosmético: sin él, una app a la que le falte la
        // variable recibe undefined, setTimeout lo toma como 0 y el race pierde
        // contra su propio timeout antes de que la API conteste. Se ve como un
        // error de credenciales con un 200 en la pestaña de red.
        process.env.REACT_APP_API_TIMEOUT || 30000,
      );
    }),
  ])
    .then((response) => {
      // Cuando resuelve la carrera, verifica el status de la respuesta de la API
      // Si es 200 o 201, es exitosa y llama al callback
      if (response.status === 204) {
        callbackSuccess([{ msg: "Respuesta exitosa" }]);
      } else if (response.status >= 200 && response.status < 300) {
        response.blob().then((data) => {
          const blobUrl = URL.createObjectURL(data);

          // Create a link element
          const link = document.createElement("a");

          // Set link's href to point to the Blob URL
          link.href = blobUrl;
          link.download = filename;

          // Append link to the body
          document.body.appendChild(link);

          // Dispatch click event on the link
          // This is necessary as link.click() does not work on the latest firefox
          link.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            }),
          );

          // Remove link from body
          document.body.removeChild(link);
          callbackSuccess(data);
        });
      } else {
        response.json().then((data) => {
          data.status = response.status;
          callbackFail(data);
        });
      }
    })
    .catch((e) => {
      console.log(e);
      var response = {
        status: 500,
        msg: "Ocurrió un error, intente nuevamente más tarde",
      };
      callbackFail(response);
    });
}

export function compareSemver(a, b) {
  const [ma, mi, pa] = a.split(".").map(Number);
  const [mb, mib, pb] = b.split(".").map(Number);
  return ma !== mb ? ma - mb : mi !== mib ? mi - mib : pa - pb;
}

// Function called when the API call responds with something other than 200 or 201
export function APIerror(response) {
  console.log(response);
  // Verifies if it's a server issue
  if (response.status >= 500) {
    alert(response.msg);
    window.location.href = "/tablero";
    return;
  }

  // Verifies if the issue is with permissions
  if (response.status === 401 || response.status === 403) {
    alert(response.msg);
    deleteFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);
    window.location.href = urlLogin();
    return;
  }

  // If the error is different, show the prompt but stay
  alert(response.msg);
  return;
}

// "de" + el nombre del organismo que devuelve useOrganismo(): "de Coleoptera",
// o "del organismo" cuando no hay nombre cargado
export const deOrganismo = (organismo) =>
  organismo === "el organismo" ? "del organismo" : `de ${organismo}`;

// Archivos adjuntos a una interacción de hallazgo, como lista. Las respuestas
// nuevas guardan varios en `evidencias`; las viejas, uno en evidenciaArchivoId.
export function evidenciasDeInteraccion(interaccion) {
  if (Array.isArray(interaccion?.evidencias)) {
    return interaccion.evidencias.map((e) => ({ archivoId: e.archivoId, nombre: e.nombre, hash: e.hash }));
  }
  if (interaccion?.evidenciaArchivoId) {
    return [
      {
        archivoId: interaccion.evidenciaArchivoId,
        nombre: interaccion.evidenciaNombreArchivo,
        hash: interaccion.evidenciaArchivoHash
      }
    ];
  }
  return [];
}

// Como downloadFile, pero en vez de descargar devuelve el archivo (blob) a
// callbackSuccess, para cuando el nombre del archivo depende de su tipo (p. ej.
// los comprobantes de facturas del portal del cliente: PDF de Biller o el
// archivo que subió el organismo).
export function obtenerArchivo(endpoint, callbackSuccess, callbackFail) {
  const url = urlBaseAPI() + "/" + endpoint;
  const accessToken = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);
  fetch(url, { method: "GET", headers: { Authorization: "Bearer " + accessToken } })
    .then((response) => {
      if (response.status === 200 || response.status === 201) {
        return response.blob().then((blob) => callbackSuccess(blob));
      }
      return response
        .json()
        .then((data) => callbackFail(data))
        .catch(() => callbackFail([{ msg: "No se pudo obtener el archivo" }]));
    })
    .catch(() => callbackFail([{ msg: "No se pudo obtener el archivo" }]));
}
